import { Routes, Route } from 'react-router-dom'
import { Suspense, lazy } from 'react'

// Guards
import WalletPage from "./pages/WalletPage";
const ProtectedRoute = lazy(() => import('./components/guards/ProtectedRoute'))
const AdminRoute = lazy(() => import('./components/guards/AdminRoute'))

// Layouts
const DashboardLayout = lazy(() => import('./components/layout/DashboardLayout'))
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'))
const AuthLayout = lazy(() => import('./components/layout/AuthLayout'))

// Public Pages
const LandingPage = lazy(() => import('./pages/public/LandingPage'))
const LoginPage = lazy(() => import('./pages/public/LoginPage'))
const SignupPage = lazy(() => import('./pages/public/SignupPage'))
const ForgotPasswordPage = lazy(() => import('./pages/public/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/public/ResetPasswordPage'))
const TermsPage = lazy(() => import('./pages/public/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/public/PrivacyPage'))
const RiskDisclaimerPage = lazy(() => import('./pages/public/RiskDisclaimerPage'))

// Dashboard Pages
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'))
const BotsPage = lazy(() => import('./pages/dashboard/BotsPage'))
const CreateBotPage = lazy(() => import('./pages/dashboard/CreateBotPage'))
const BotDetailPage = lazy(() => import('./pages/dashboard/BotDetailPage'))
const GasPage = lazy(() => import('./pages/dashboard/GasPage'))
const SubscriptionPage = lazy(() => import('./pages/dashboard/SubscriptionPage'))
const ReferralsPage = lazy(() => import('./pages/dashboard/ReferralsPage'))
const P2PPage = lazy(() => import('./pages/dashboard/P2PPage'))
const P2POfferPage = lazy(() => import('./pages/public/P2POfferPage'))
const SettingsPage = lazy(() => import('./pages/dashboard/SettingsPage'))

// Admin Pages
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'))
const AdminUserDetailPage = lazy(() => import('./pages/admin/AdminUserDetailPage'))
const AdminBotsPage = lazy(() => import('./pages/admin/AdminBotsPage'))
const AdminGasPage = lazy(() => import('./pages/admin/AdminGasPage'))
const AdminManualPaymentsPage = lazy(() => import('./pages/admin/AdminManualPaymentsPage'))
const AdminWithdrawalsPage = lazy(() => import('./pages/admin/AdminWithdrawalsPage'))
const AdminSubscriptionsPage = lazy(() => import('./pages/admin/AdminSubscriptionsPage'))
const AdminReferralsPage = lazy(() => import('./pages/admin/AdminReferralsPage'))
const AdminWalletOperationsPage = lazy(() => import('./pages/admin/AdminWalletOperationsPage'))
const AdminCustomerCryptoWalletsPage = lazy(() => import('./pages/admin/AdminCustomerCryptoWalletsPage'))
const AdminCustomerWalletRecoveryPage = lazy(() => import('./pages/admin/AdminCustomerWalletRecoveryPage'))
const AdminSecuritySettingsPage = lazy(() => import('./pages/admin/AdminSecuritySettingsPage'))
const AdminAIHedgeAnalyticsPage = lazy(() => import('./pages/admin/AdminAIHedgeAnalyticsPage'))
const AdminP2PPage = lazy(() => import('./pages/admin/AdminP2PPage'))

// Loading component
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center bg-background-900">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
  </div>
)

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/risk-disclaimer" element={<RiskDisclaimerPage />} />
      <Route path="/p2p/offer/:id" element={<P2POfferPage />} />

        {/* Auth (centered card layout) */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Dashboard (protected) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="bots" element={<BotsPage />} />
            <Route path="bots/create" element={<CreateBotPage />} />
            <Route path="bots/:id" element={<BotDetailPage />} />
            <Route path="gas" element={<GasPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="referrals" element={<ReferralsPage />} />
            <Route path="p2p" element={<P2PPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="/wallet" element={<DashboardLayout />}>
            <Route index element={<WalletPage />} />
          </Route>
        </Route>

        {/* Admin (protected + admin role) */}
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:id" element={<AdminUserDetailPage />} />
            <Route path="bots" element={<AdminBotsPage />} />
            <Route path="gas" element={<AdminGasPage />} />
            <Route path="manual-payments" element={<AdminManualPaymentsPage />} />
            <Route path="withdrawals" element={<AdminWithdrawalsPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="referrals" element={<AdminReferralsPage />} />
            <Route path="wallet-operations" element={<AdminWalletOperationsPage />} />
            <Route path="crypto-wallets" element={<AdminCustomerCryptoWalletsPage />} />
            <Route path="crypto-wallet-recovery" element={<AdminCustomerWalletRecoveryPage />} />
            <Route path="security" element={<AdminSecuritySettingsPage />} />
            <Route path="ai-hedge-analytics" element={<AdminAIHedgeAnalyticsPage />} />
    <Route path="p2p" element={<AdminP2PPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
